import React from "react";
import {
  Flex,
  Link,
  ImageResponsive,
  styled,
  theme,
  themeGet,
  Box,
  FlexProps,
} from "@catchoftheday/cg-components";

import { SOURCE_TYPES_EMARSYS } from "@catchoftheday/analytics";
import { AvailabilityTags } from "../AvailabilityTags";
import { ProductItemType } from "../../types";
import { ProductPrice } from "../ProductPrice";
import { AddToCart } from "./AddToCart/AddToCart";

import { ProductCardBrand } from "./ProductCardBrand";
import { ProductCardTitle } from "./ProductCardTitle";
import { ProductCardTags } from "./ProductCardTags";
import { ProductCardRating } from "./ProductCardRating";
import { COTD_WEBSITE_CDN_URL } from "@search-app/config";

const PRODUCT_IMAGE_ASPECT_RATIO = 81.818;

type ProductCardProps = Omit<FlexProps, "ref"> & {
  lazyLoad?: boolean;
  visibleActionButton?: boolean;
  productItem: ProductItemType;
};

type ProductCardContainerProps = FlexProps & { visibleActionButton?: boolean };

// TODO: Change this to use a component selector rather than classname selector when upgraded to Emotion 11
const ProductCardContainer = styled(Flex)<ProductCardContainerProps>`
  .product--buy-form--container {
    transition: bottom 0.2s ease-out;
    position: absolute;
    bottom: ${(props) => (!props.visibleActionButton ? "-120px" : 0)};
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 5;
  }

  @media only screen and (min-width: ${themeGet("breakpoints.lg")}) {
    &:hover {
      .product--buy-form--container {
        bottom: 0;
      }
      > div {
        border: 1px solid ${themeGet("colors.secondary")};
      }
    }
  }
`;

const PriceLink = styled(Link)`
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

export const ProductCard = ({
  lazyLoad,
  productItem,
  visibleActionButton,
  ...flexProps
}: ProductCardProps) => {
  const {
    attributeName,
    averageRating,
    brand,
    clubCatchEligible,
    clubCatchExclusive,
    clubCatchPrice,
    discountPercent,
    eventId,
    freeShippingAvailable,
    id,
    images,
    isPersonalisedProduct,
    offerId,
    promotionDescription,
    priceDisplayType,
    quantityForSale,
    ratingCount,
    retailPrice,
    savingsLabel,
    selectedOfferDiscountEndDate,
    sellable,
    sellerId,
    sellPrice,
    showClubCatchPrice,
    showFromLabel,
    showPromotionPrice,
    showPromotionTimer,
    showRetailPrice,
    showSavingsLabel,
    showWasLabel,
    sourceId,
    sourcePosition,
    sourceType,
    sourcePlatform,
    title,
    trackEventToken,
    urgentMessage,
    showShortDatedBadge,
    wasPrice,
    imageLocation,
  } = productItem;

  // Normally we'd send a product interaction event ourselves when the user clicks on a product,
  // however that requires us to fetch more context from Snowplow, which means we'd need to get
  // in the way of the link. Instead, we add the necessary information to the URL and let the
  // recipient fire the events on page load
  const url = getEffectiveUrl(productItem);

  const emarsysAttributionTag =
    typeof sourceType === "number" && SOURCE_TYPES_EMARSYS.includes(sourceType)
      ? id
      : undefined;

  return (
    <ProductCardContainer
      id={`${id}`}
      paddingX={{ xs: "2px", md: "10px" }}
      textAlign="center"
      data-scarabitem={emarsysAttributionTag}
      visibleActionButton={visibleActionButton}
      {...flexProps}
    >
      <Flex
        flexDirection="column"
        width="100%"
        position="relative"
        overflow="hidden"
        border={`1px solid ${theme.colors.ui.borderColor}`}
        backgroundColor="white"
        fontWeight="light"
        data-ts="product-card"
      >
        <Box display="block" position="relative" overflow="hidden" mb={0}>
          <Link
            href={url}
            title={`View ${title}`}
            hoverColor="currentColor"
            data-product-id={id}
            data-ts="product-card-link"
          >
            <ImageResponsive
              alt={title}
              lazyLoad={lazyLoad}
              sourceWidths={[100, 200, 300, 400, 500]}
              src={`${COTD_WEBSITE_CDN_URL}/${imageLocation}`}
              ratio={PRODUCT_IMAGE_ASPECT_RATIO}
            />

            <AvailabilityTags
              onePassExclusive={clubCatchExclusive}
              discountPercent={discountPercent}
              quantityForSale={quantityForSale}
              urgentMessage={urgentMessage}
              showShortDatedBadge={showShortDatedBadge}
            />
          </Link>
          {!visibleActionButton && (
            <AddToCart
              attributeName={attributeName}
              eventId={eventId}
              id={id}
              images={images}
              isPersonalisedProduct={isPersonalisedProduct}
              offerId={offerId}
              quantityForSale={quantityForSale}
              sellerId={sellerId}
              sellPrice={sellPrice}
              sourceId={sourceId}
              sourcePosition={sourcePosition}
              sourceType={sourceType}
              sourcePlatform={sourcePlatform}
              title={title}
              trackEventToken={trackEventToken}
              url={url}
              onePassExclusive={clubCatchExclusive}
            />
          )}
        </Box>
        <Flex
          flexGrow={1}
          flexDirection="column"
          justifyContent="space-between"
          py="6px"
        >
          <Box>
            <ProductCardTitle url={url} productId={id} title={title} />

            <ProductCardBrand brand={brand} />
          </Box>
          <PriceLink
            display="table"
            px={{ md: "10px" }}
            pb="6px"
            width="100%"
            textDecoration="none"
            href={url}
            title={`View ${title}`}
            data-product-id={id}
            hoverColor="inherit"
          >
            <ProductPrice
              onePassExclusive={clubCatchExclusive}
              onePassPrice={clubCatchPrice}
              promotionDescription={promotionDescription}
              priceDisplayType={priceDisplayType}
              retailPrice={retailPrice}
              savingsLabel={savingsLabel}
              selectedOfferDiscountEndDate={selectedOfferDiscountEndDate}
              sellable={sellable}
              sellPrice={sellPrice}
              showOnePassPrice={showClubCatchPrice}
              showFromLabel={showFromLabel}
              showPromotionPrice={showPromotionPrice}
              showPromotionTimer={showPromotionTimer}
              showRetailPrice={showRetailPrice}
              showSavingsLabel={showSavingsLabel}
              showWasLabel={showWasLabel}
              wasPrice={wasPrice}
            />
          </PriceLink>
          <Flex
            flexDirection={"row"}
            justifyContent="space-around"
            px={{ xs: "2px", md: "36px" }}
            alignItems="center"
            minHeight="45px"
          >
            {(clubCatchEligible || freeShippingAvailable) && (
              <ProductCardTags
                onePassEligible={clubCatchEligible}
                freeShippingAvailable={freeShippingAvailable}
              />
            )}
            {averageRating >= 1 && (
              <ProductCardRating
                url={url}
                productId={id}
                rating={averageRating}
                ratingCount={ratingCount}
              />
            )}
          </Flex>
          {visibleActionButton && (
            <AddToCart
              attributeName={attributeName}
              eventId={eventId}
              id={id}
              images={images}
              isPersonalisedProduct={isPersonalisedProduct}
              offerId={offerId}
              quantityForSale={quantityForSale}
              sellerId={sellerId}
              sellPrice={sellPrice}
              sourceId={sourceId}
              sourcePosition={sourcePosition}
              sourceType={sourceType}
              title={title}
              trackEventToken={trackEventToken}
              url={url}
              onePassExclusive={clubCatchExclusive}
            />
          )}
        </Flex>
      </Flex>
    </ProductCardContainer>
  );

  function getEffectiveUrl({
    id,
    sourceId,
    sourceType,
    sourcePosition,
    offerId,
    url,
  }: ProductItemType): string {
    const existingParams = getParams(url);
    const required = {
      pid: id,
      sid: sourceId,
      st: sourceType,
      sp: sourcePosition,
      oid: offerId,
      // These rely on information not available to the ProductCard
      // st: activeShop
      // cts: activeShopGroup
      // ctt: activeShopItem
    };
    const extraParams = Object.keys(required)
      .filter((key) => existingParams[key] == null)
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(required[key])}`
      )
      .join("&");

    return `${url}&emit_product_interaction_events&${extraParams}`;
  }

  function getParams(url: string) {
    const queryParts = url
      .replace(/.*\?/, "")
      .split("&")
      .map((part) => part.split("="));

    return Object.fromEntries(queryParts);
  }
};
